// The encore meter on the song playing now: likes so far towards its next play in a row
// ("♥ 4/12 for an encore"), from /api/repeats/now and live "likes" messages.
import { useEffect } from "react";
import { MAX_PLAYS } from "@eb/shared";
import { api } from "../api";
import { useLive, type NowLikes } from "../live";

/** Fetch the current song's likes (on slot changes and after your own like). */
export async function refreshNowLikes(): Promise<void> {
  try {
    const r = await api<{ now: NowLikes | null }>("GET", "/api/repeats/now");
    useLive.getState().set({ nowLikes: r.now });
  } catch {
    /* the meter just stays as it was */
  }
}

export function EncoreMeter({ day, slot, loopId }: { day: number; slot: number; loopId: string }) {
  const n = useLive((s) => s.nowLikes);
  useEffect(() => {
    void refreshNowLikes();
  }, [day, slot, loopId]);
  if (!n || n.day !== day || n.slot !== slot || n.loopId !== loopId) return null;
  if (n.need === null)
    return n.play > 1 ? (
      <span className="encore-meter done" data-testid="encore-meter">
        ↻ Encore — play {n.play} of {MAX_PLAYS}
      </span>
    ) : null;
  const share = Math.min(1, n.likes / n.need);
  return (
    <span
      className={`encore-meter${share >= 1 ? " full" : ""}`}
      data-testid="encore-meter"
      title="Likes while this song plays earn it another play right after"
    >
      <span className="encore-text">
        ♥ {n.likes}/{n.need} {share >= 1 ? "— encore!" : n.play > 1 ? `for play ${n.play + 1}` : "for an encore"}
      </span>
      <span className="encore-bar" aria-hidden="true">
        <span style={{ width: `${Math.round(share * 100)}%` }} />
      </span>
    </span>
  );
}
